import type { JSX } from 'react';
import type { WireUserRole } from '@logistics/contracts';
import { Activity, Database, HeartPulse, LayoutDashboard, MessageSquare } from 'lucide-react';
import type { LucideIcon } from 'lucide-react';
import { useOverview } from '../overview/use-overview';
import { tabItems } from './routes';
import type { PageId } from './routes';
import { navigate } from './use-route';

/** Icons in place of the design's OV/CH/SRC/ACT/HLT abbreviations. */
const ICONS: Partial<Record<PageId, LucideIcon>> = {
  overview: LayoutDashboard,
  chat: MessageSquare,
  sources: Database,
  activity: Activity,
  health: HeartPulse,
};

/**
 * The phone's bottom bar (design L750): five tabs, an icon chip over a label,
 * the active one in the accent. It sits below the overlays so a bottom sheet's
 * footer is never hidden behind it.
 */
export function TabBar({ role, active }: { role: WireUserRole; active: PageId }): JSX.Element {
  const errors = useOverview().data?.sources.errors ?? 0;
  const items = tabItems(role, errors);
  return (
    <nav
      aria-label="Pages"
      className="border-line fixed inset-x-0 bottom-0 z-[22] grid gap-0.5 border-t bg-[color-mix(in_oklch,var(--bg)_88%,transparent)] px-2 pt-2 pb-[calc(8px+env(safe-area-inset-bottom))] backdrop-blur-[16px] tab:hidden"
      style={{ gridTemplateColumns: `repeat(${items.length},minmax(0,1fr))` }}
    >
      {items.map((item) => {
        const on = item.page === active;
        const Icon = ICONS[item.page];
        return (
          <button
            key={item.page}
            type="button"
            aria-current={on ? 'page' : undefined}
            onClick={() => navigate({ page: item.page })}
            className="relative flex min-h-[52px] flex-col items-center justify-center gap-[5px] rounded-xl border-0 bg-transparent px-0.5 py-1 transition-colors"
            style={{ color: on ? 'var(--acc)' : 'var(--mute)' }}
          >
            <span
              className="grid h-6 w-[34px] place-items-center rounded-lg transition-colors"
              style={{ background: on ? 'var(--accSoft)' : 'transparent' }}
            >
              {Icon !== undefined && <Icon aria-hidden="true" size={18} strokeWidth={1.75} />}
            </span>
            <span className="text-[10.5px] font-medium tracking-[.01em]">{item.label}</span>
            {item.badge > 0 && (
              <span className="bg-bad absolute top-0.5 right-3.5 grid h-4 min-w-4 place-items-center rounded-full px-1 font-mono text-[10px] text-white">
                {item.badge}
              </span>
            )}
          </button>
        );
      })}
    </nav>
  );
}
